import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { PopoverContent } from './PopoverContent';
import { PopoverRoot } from './PopoverRoot';
import { PopoverTrigger } from './PopoverTrigger';

type Props = {
  /** トリガー要素 */
  children: ReactNode;
  /** ポップオーバーの内容 */
  content: ReactNode;
  /** 開閉状態（制御コンポーネントとして使用する場合） */
  open?: boolean;
  /** 開閉状態変更時のコールバック */
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** 無効状態 */
  disabled?: boolean;
  /** トリガーのカスタムクラス名 */
  triggerClassName?: string;
  /** コンテンツのカスタムクラス名 */
  contentClassName?: string;
  /** コンテンツのカスタムスタイル */
  contentStyle?: CSSProperties;
  /** コンテンツの配置位置 */
  contentAlign?: 'start' | 'end' | 'center';
  /** コンテンツのオフセット */
  contentSideOffset?: number;
  /** コンテンツが開いた時のオートフォーカス制御 */
  onOpenAutoFocus?: ((event: Event) => void) | undefined;
  /** コンテンツでのキーボードイベント */
  onKeyDown?: ((event: KeyboardEvent<HTMLDivElement>) => void) | undefined;
};

/**
 * Popover 集約コンポーネント
 *
 * シンプルな使用ケース向けの高レベルコンポーネント。
 * より複雑なケースでは、PopoverRoot, PopoverTrigger, PopoverContent を直接使用してください。
 *
 * @example
 * ```tsx
 * <Popover
 *   content={<div>ポップオーバーの内容</div>}
 *   contentAlign="start"
 * >
 *   <Button>開く</Button>
 * </Popover>
 * ```
 */
export const Popover = ({
  children,
  content,
  open,
  onOpenChange,
  disabled,
  triggerClassName,
  contentClassName,
  contentStyle,
  contentAlign = 'center',
  contentSideOffset = 4,
  onOpenAutoFocus,
  onKeyDown,
}: Props) => {
  const rootProps = {
    ...(open !== undefined && { open }),
    ...(onOpenChange !== undefined && { onOpenChange }),
  };

  const contentProps = {
    ...(onOpenAutoFocus !== undefined && { onOpenAutoFocus }),
    ...(onKeyDown !== undefined && { onKeyDown }),
  };

  return (
    <PopoverRoot {...rootProps}>
      <PopoverTrigger
        asChild
        disabled={disabled}
        className={cn(
          'focus:ring-2 focus:ring-focus-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
          triggerClassName,
        )}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        align={contentAlign}
        sideOffset={contentSideOffset}
        className={cn('bg-surface-base', contentClassName)}
        style={contentStyle}
        {...contentProps}
      >
        {content}
      </PopoverContent>
    </PopoverRoot>
  );
};
